<template>
  <div class="strategy-detail-page p-3 p-md-4" :class="theme === 'light' ? 'light-theme' : 'dark-theme'">
    <!-- 返回按钮与页面标题 -->
    <div class="d-flex align-items-center justify-content-between mb-4">
      <b-button variant="link" class="text-muted p-0 d-flex align-items-center text-decoration-none hover-primary" @click="$router.back()">
        <b-icon icon="arrow-left-short" font-scale="1.6" class="mr-1"></b-icon> 返回题材表格
      </b-button>
      <b-badge variant="soft-warning" class="px-3 py-2 rounded-pill premium-badge shadow-sm">
        <i class="fas fa-crown mr-1"></i> 暗金 V2 旗舰版
      </b-badge>
    </div>

    <!-- 策略核心介绍卡片 (黑金尊贵质感) -->
    <b-card :class="['premium-intro-card border-0 mb-4 overflow-hidden shadow-lg position-relative', theme === 'light' ? 'text-dark' : 'text-white']">
      <div class="premium-card-bg"></div>
      <b-card-body class="p-4 position-relative z-index-1">
        <!-- 头部行：标题、描述与总权益 -->
        <div class="d-flex flex-wrap justify-content-between align-items-center pb-3 mb-3 premium-divider">
          <div class="strategy-title-area">
            <div class="d-flex align-items-center mb-1">
              <h3 class="font-weight-bold gold-text mb-0">Alpha-S 盘中动能沙盘 V2 旗舰版</h3>
              <b-button v-if="isAdmin && hasPermission" :variant="theme === 'light' ? 'outline-dark' : 'outline-light'" class="ml-3 rounded-pill px-3" size="sm" @click="$router.push('/wonder/strategy/panorama')">
                <b-icon icon="display" class="mr-1"></b-icon> 全景解析
              </b-button>
              <b-button v-if="isAdmin && hasPermission" variant="outline-warning" class="ml-2 rounded-pill px-3" size="sm" @click="generateShareLink">
                <b-icon icon="share" class="mr-1"></b-icon> 生成分享链接
              </b-button>
            </div>
            <p class="intro-desc small opacity-8 mb-0">
              基于**核心题材筛选法**进行早盘 09:30-10:30 多空博弈流监测。实现超额 Alpha 。注意：模拟交易，仅供学习研究，不具有投资参考价值。
            </p>
          </div>
          <div class="equity-summary mt-3 mt-md-0 text-md-right">
            <div class="d-flex align-items-baseline justify-content-md-end">
              <span class="x-small gold-text font-weight-bold mr-2">当前账户总权益</span>
              <h3 class="font-weight-bold mb-0 text-nowrap" :class="latestEquity >= 1000000 ? 'pnl-red' : 'pnl-green'">
                {{ formatMoney(latestEquity) }}
                <span class="font-weight-bold ml-1" style="font-size: 0.7em;">
                  ({{ realTimeReturnPct >= 0 ? '+' : '' }}{{ realTimeReturnPct.toFixed(2) }}%)
                </span>
              </h3>
            </div>
            <span class="x-small text-muted d-block mt-1">起步本金: 1,000,000.00 CNY</span>
          </div>
        </div>

        <!-- 底部行：核心业绩指标 & 月度日历统计 -->
        <div class="row align-items-center">
          <!-- 左侧：4个核心指标 -->
          <div class="col-12 col-lg-7 pr-lg-4 premium-metrics-col">
            <div class="row text-center text-md-left">
              <div class="col-6 col-sm-3 mb-3 mb-sm-0">
                <span class="x-small premium-metric-label d-block">模拟收益率</span>
                <span class="metric-value font-weight-bold font-scale-1.3 d-block mt-2" :class="realTimeReturnPct >= 0 ? 'pnl-red' : 'pnl-green'">
                  {{ realTimeReturnPct >= 0 ? '+' : '' }}{{ realTimeReturnPct.toFixed(2) }}%
                </span>
              </div>
              <div class="col-6 col-sm-3 mb-3 mb-sm-0">
                <span class="x-small premium-metric-label d-block">胜率</span>
                <span class="metric-value font-weight-bold text-warning font-scale-1.3 d-block mt-2">
                  {{ performance.win_rate_pct.toFixed(2) }}%
                </span>
              </div>
              <div class="col-6 col-sm-3">
                <span class="x-small premium-metric-label d-block">仓位|单笔均收</span>
                <span class="metric-value font-weight-bold text-light font-scale-1.2 d-block mt-2 text-truncate">
                  2.5层|<span :class="performance.avg_return_pct >= 0 ? 'pnl-red' : 'pnl-green'">{{ performance.avg_return_pct >= 0 ? '+' : '' }}{{ performance.avg_return_pct.toFixed(2) }}%</span>
                </span>
              </div>
              <div class="col-6 col-sm-3">
                <span class="x-small premium-metric-label d-block">粗浅卖出</span>
                <span class="metric-value font-weight-bold text-light font-scale-1.0 d-block mt-2 text-truncate" title="涨停/09:45下水/尾盘">
                  涨停/09:45下水/尾盘
                </span>
              </div>
            </div>
          </div>

          <!-- 右侧：月度收益日历统计 (水平并排) -->
          <div class="col-12 col-lg-5 mt-4 mt-lg-0 pl-lg-4">
            <div v-if="equityData.length > 0" class="mini-calendar-container d-flex flex-row justify-content-start justify-content-lg-end gap-3 flex-wrap">
              <div v-for="yearData in monthlyYields" :key="yearData.year" class="mini-year-group d-flex align-items-center">
                <span class="mini-year-title font-weight-bold mr-2 text-nowrap">{{ yearData.year }}年</span>
                <div class="mini-month-grid">
                  <div 
                    v-for="monthData in yearData.months" 
                    :key="monthData.month" 
                    class="mini-month-box d-flex flex-column align-items-center justify-content-center"
                    :class="!monthData.hasData ? 'bg-mini-none' : (monthData.yield >= 0 ? 'bg-mini-up' : 'bg-mini-down')"
                    v-b-tooltip.hover.html="getTooltipContent(yearData.year, monthData)"
                  >
                    <div class="d-flex align-items-center justify-content-center w-100 mb-0.5">
                      <i v-if="monthData.isBest" class="fas fa-thumbs-up mini-best-icon mr-1"></i>
                      <span class="mini-month-label">{{ monthData.month }}月</span>
                    </div>
                    <span class="mini-month-yield">
                      <span v-if="!monthData.hasData">-</span>
                      <span v-else>{{ monthData.yield >= 0 ? '+' : '' }}{{ monthData.yield.toFixed(2) }}%</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- 交互式资金曲线图卡片 -->
    <b-card no-body class="equity-chart-card shadow-sm mb-4 overflow-hidden custom-card">
      <div class="card-header bg-transparent border-0 pt-4 px-4 d-flex flex-wrap align-items-center justify-content-between">
        <div>
          <h5 class="font-weight-bold mb-1">累计净值与资金曲线</h5>
          <p class="text-muted small mb-0">点击下方曲线中的任意日期，可自动定位并查询当天的交易流水和持仓快照。模拟交易，技术交流 仅供参考，不构成投资建议！</p>
        </div>
        <div class="d-flex align-items-center mt-2 mt-sm-0">
          <b-button size="sm" variant="outline-warning" class="rounded-pill px-3 mr-2" @click="refreshAllData" :disabled="chartLoading">
            <b-icon icon="arrow-clockwise" :animation="chartLoading ? 'spin' : ''"></b-icon> 刷新曲线
          </b-button>
        </div>
      </div>
      
      <b-card-body class="p-3 position-relative responsive-chart-body">
        <div v-if="chartLoading" class="chart-loading-overlay d-flex flex-column align-items-center justify-content-center h-100">
          <b-spinner variant="warning"></b-spinner>
          <span class="text-muted mt-2 small">正在读取并烘焙每日资产快照...</span>
        </div>
        <div v-else-if="!equityData.length" class="empty-chart d-flex flex-column align-items-center justify-content-center h-100">
          <b-icon icon="graph-up" font-scale="3" class="text-muted opacity-3 mb-2"></b-icon>
          <span class="text-muted small">暂无净值曲线数据</span>
        </div>
        <v-chart 
          v-else 
          ref="equityChart" 
          :options="chartOptions" 
          :theme="theme === 'dark' ? 'site-dark' : ''" 
          autoresize 
          @click="onChartClick" 
          style="width: 100%; height: 100%;" 
        />
      </b-card-body>
    </b-card>

    <!-- 交易流水与持仓详情包裹器（有权限可正常查看，无权限磨砂锁定） -->
    <div v-if="hasPermission" class="position-relative mt-4" style="min-height: 380px;">
      <!-- 特定交易日详情区块 -->
      <div class="row align-items-center mb-3">
      <div class="col-12 col-md-6 d-flex align-items-center">
        <b-icon icon="calendar-date" class="text-warning mr-2" font-scale="1.3"></b-icon>
        <h5 class="font-weight-bold mb-0">
          日期数据审计：<span class="gold-text font-scale-1.1">{{ selectedDate || '未选择' }}</span>
        </h5>
      </div>
      <div class="col-12 col-md-6 mt-2 mt-md-0 d-flex justify-content-md-end align-items-center">
        <span class="small text-muted mr-2">手动选择审计日期：</span>
        <b-form-input 
          type="date" 
          v-model="selectedDate" 
          size="sm" 
          class="custom-datepicker rounded-pill px-3 shadow-sm mr-2" 
          style="width: 170px;"
          @change="fetchDailyDetails"
        ></b-form-input>
        <b-button size="sm" variant="primary" class="rounded-pill px-3 shadow-sm" @click="fetchDailyDetails" :disabled="detailsLoading">
          查询
        </b-button>
      </div>
    </div>

    <!-- 交易记录 & 持仓快照双列表区域 -->
    <div class="row">
      <!-- 交易流水列表 -->
      <div class="col-12 col-lg-6 mb-4">
        <b-card no-body class="shadow-sm h-100 overflow-hidden custom-card">
          <div class="card-header bg-transparent border-bottom p-3 d-flex align-items-center justify-content-between custom-card-header">
            <span class="font-weight-bold">
              <b-icon icon="receipt" class="text-info mr-2"></b-icon> 当日交易流水
            </span>
            <b-badge variant="soft-info" pill>{{ dailyRecords.length }} 笔成交</b-badge>
          </div>
          
          <b-card-body class="p-0">
            <div v-if="detailsLoading" class="text-center py-5">
              <b-spinner variant="info" class="mb-2"></b-spinner>
              <p class="text-muted small mb-0">正在提取流水账单...</p>
            </div>
            <div v-else-if="!dailyRecords.length" class="text-center py-5">
              <b-icon icon="inbox" font-scale="2" class="text-muted opacity-3 mb-2"></b-icon>
              <p class="text-muted small mb-0">{{ selectedDate }} 这一天没有产生交易流水 ☕</p>
            </div>
            <div v-else class="table-responsive">
              <b-table 
                small 
                striped 
                hover 
                :items="dailyRecords" 
                :fields="recordFields" 
                class="mb-0 text-nowrap align-middle"
                thead-class="bg-soft-header text-muted x-small"
              >
                <!-- 操作时间 -->
                <template #cell(trade_time)="{ item }">
                  <span class="x-small font-weight-bold text-info">{{ item.trade_time || '-' }}</span>
                </template>

                <!-- 股票代码/名字 -->
                <template #cell(symbol_name)="{ item }">
                  <div class="d-flex flex-column clickable-stock-cell" @click="openSidebar(item)">
                    <span class="font-weight-bold text-truncate" :class="theme === 'light' ? 'text-dark' : 'text-white'" style="max-width: 120px;">
                      {{ item.symbol_name }}
                    </span>
                    <span class="x-small text-muted">{{ item.symbol_code }}</span>
                  </div>
                </template>

                <!-- 成交方向 -->
                <template #cell(trade_type)="{ item }">
                  <b-badge :variant="item.trade_type === '买入' ? 'soft-danger' : 'soft-success'" class="px-2 py-1 rounded">
                    {{ item.trade_type }}
                  </b-badge>
                </template>

                <!-- 成交价格 -->
                <template #cell(trade_price)="{ item }">
                  <span class="font-weight-bold">{{ item.trade_price.toFixed(2) }}</span>
                </template>

                <!-- 成交总额 -->
                <template #cell(trade_balance)="{ item }">
                  <span>{{ formatCompactMoney(item.trade_balance) }}</span>
                </template>

                <!-- 当日盈亏 -->
                <template #cell(today_pnl)="{ item }">
                  <div class="d-flex flex-column text-right">
                    <span 
                      class="font-weight-bold" 
                      :class="item.today_income_balance >= 0 ? 'pnl-red' : 'pnl-green'"
                    >
                      {{ item.today_income_balance >= 0 ? '+' : '' }}{{ item.today_income_balance.toFixed(2) }}
                    </span>
                    <span 
                      class="x-small font-weight-bold" 
                      :class="item.today_income_balance >= 0 ? 'pnl-red' : 'pnl-green'"
                    >
                      {{ item.today_income_balance >= 0 ? '+' : '' }}{{ (item.today_income_ratio || 0.0).toFixed(2) }}%
                    </span>
                  </div>
                </template>

                <!-- 总盈亏 -->
                <template #cell(total_pnl)="{ item }">
                  <div v-if="item.trade_type === '卖出'" class="d-flex flex-column text-right">
                    <span 
                      class="font-weight-bold" 
                      :class="item.income_balance >= 0 ? 'pnl-red' : 'pnl-green'"
                    >
                      {{ item.income_balance >= 0 ? '+' : '' }}{{ item.income_balance.toFixed(2) }}
                    </span>
                    <span 
                      class="x-small font-weight-bold" 
                      :class="item.income_balance >= 0 ? 'pnl-red' : 'pnl-green'"
                    >
                      {{ item.income_balance >= 0 ? '+' : '' }}{{ ((item.income_balance / (item.trade_balance - item.income_balance)) * 100).toFixed(2) }}%
                    </span>
                  </div>
                  <span v-else class="text-muted">-</span>
                </template>

                <!-- 双边交易损耗 -->
                <template #cell(commission_balance)="{ item }">
                  <span class="text-muted x-small">{{ item.commission_balance.toFixed(2) }}</span>
                </template>
              </b-table>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <!-- 持仓状态快照 -->
      <div class="col-12 col-lg-6 mb-4">
        <b-card no-body class="shadow-sm h-100 overflow-hidden custom-card">
          <div class="card-header bg-transparent border-bottom p-3 d-flex align-items-center justify-content-between custom-card-header">
            <span class="font-weight-bold">
              <b-icon icon="wallet2" class="text-success mr-2"></b-icon> 当日持仓快照
            </span>
            <b-badge variant="soft-success" pill>{{ dailyPositions.length }} 处于活跃/变更</b-badge>
          </div>

          <b-card-body class="p-0">
            <div v-if="detailsLoading" class="text-center py-5">
              <b-spinner variant="success" class="mb-2"></b-spinner>
              <p class="text-muted small mb-0">正在清算持仓快照...</p>
            </div>
            <div v-else-if="!dailyPositions.length" class="text-center py-5">
              <b-icon icon="briefcase" font-scale="2" class="text-muted opacity-3 mb-2"></b-icon>
              <p class="text-muted small mb-0">{{ selectedDate }} 这一天账户空仓 🌾</p>
            </div>
            <div v-else class="table-responsive">
              <b-table 
                small 
                striped 
                hover 
                :items="dailyPositions" 
                :fields="computedPositionFields" 
                class="mb-0 text-nowrap align-middle"
                thead-class="bg-soft-header text-muted x-small"
              >
                <!-- 股票代码/名字 -->
                <template #cell(symbol_name)="{ item }">
                  <div class="d-flex flex-column clickable-stock-cell" @click="openSidebar(item)">
                    <span class="font-weight-bold text-truncate" :class="theme === 'light' ? 'text-dark' : 'text-white'" style="max-width: 110px;">
                      {{ item.symbol_name }}
                    </span>
                    <span class="x-small text-muted">{{ item.symbol_code }}</span>
                  </div>
                </template>

                <!-- 持仓股数 -->
                <template #cell(total_amount)="{ item }">
                  <span>{{ item.total_amount }} 股</span>
                </template>

                <!-- 买入均价 -->
                <template #cell(avg_cost)="{ item }">
                  <span>{{ item.avg_cost.toFixed(2) }}</span>
                </template>

                <!-- 最新结算价 -->
                <template #cell(last_price)="{ item }">
                  <span class="font-weight-bold">{{ item.last_price.toFixed(2) }}</span>
                </template>

                <!-- 当前个股今日涨幅 (盘中动态) -->
                <template #cell(today_change_pct)="{ item }">
                  <span 
                    v-if="item.today_change_pct !== undefined" 
                    class="font-weight-bold" 
                    :class="item.today_change_pct >= 0 ? 'text-danger' : 'text-success'"
                  >
                    {{ item.today_change_pct >= 0 ? '+' : '' }}{{ item.today_change_pct.toFixed(2) }}%
                  </span>
                  <span v-else class="text-muted">-</span>
                </template>

                <!-- 当日盈亏 (盘中动态) -->
                <template #cell(today_pnl)="{ item }">
                  <div v-if="item.today_pnl !== undefined" class="d-flex flex-column text-right">
                    <span 
                      class="font-weight-bold" 
                      :class="item.today_pnl >= 0 ? 'text-danger' : 'text-success'"
                    >
                      {{ item.today_pnl >= 0 ? '+' : '' }}{{ item.today_pnl.toFixed(2) }}
                    </span>
                    <span 
                      class="x-small font-weight-bold" 
                      :class="item.today_pnl_pct >= 0 ? 'text-danger' : 'text-success'"
                    >
                      {{ item.today_pnl_pct >= 0 ? '+' : '' }}{{ item.today_pnl_pct.toFixed(2) }}%
                    </span>
                  </div>
                  <span v-else class="text-muted">-</span>
                </template>

                <!-- 当前持仓盈亏 / 盈亏比例 -->
                <template #cell(position_pnl)="{ item }">
                  <div class="d-flex flex-column text-right">
                    <!-- 盈亏金额 -->
                    <span 
                      class="font-weight-bold" 
                      :class="getPnlColor(item)"
                    >
                      {{ getPnlAmount(item) }}
                    </span>
                    <!-- 盈亏比例 -->
                    <span 
                      class="x-small font-weight-bold" 
                      :class="getPnlColor(item)"
                    >
                      {{ getPnlRatio(item) }}
                    </span>
                  </div>
                </template>

                <!-- 头寸状态 -->
                <template #cell(status)="{ item }">
                  <b-badge :variant="item.status === '持有中' ? 'warning' : 'secondary'" class="x-small">
                    {{ item.status }}
                  </b-badge>
                </template>

                <!-- 操作列 -->
                <template #cell(actions)="{ item }">
                  <span 
                    v-if="item.status === '持有中' && item.date !== selectedDate && !isBoughtToday(item.symbol_code)"
                    class="btn-secret"
                    @click.stop="triggerManualSell(item)"
                    title="平仓"
                  >-</span>
                  <span v-else class="text-muted x-small">-</span>
                </template>
              </b-table>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>
  </div>

  <!-- 无权限时，直接显示静态提示卡片，没有磨砂遮罩 -->
  <div v-else class="mt-4 p-5 text-center rounded shadow-sm custom-card">
    <div class="premium-badge-lock mb-4 d-inline-flex align-items-center justify-content-center rounded-circle bg-warning-10 border border-warning" style="width: 80px; height: 80px; margin: 0 auto;">
      <b-icon icon="shield-lock-fill" class="text-warning" font-scale="2.5"></b-icon>
    </div>
    <p class="text-muted-gold small max-w-400 mx-auto mb-3 leading-relaxed font-weight-bold" style="font-size: 1.05rem;">
      解锁【策略模拟V2】模块，查看实时和历史。
    </p>
    <div class="instruction-link mb-4">
      <a href="https://mp.weixin.qq.com/s/BV6xPZFO0O6-74lATXH5og" target="_blank" class="text-warning text-decoration-none">
        <b-icon icon="question-circle" class="mr-1"></b-icon>策略模拟V2使用说明
      </a>
    </div>
    <div class="d-flex align-items-center justify-content-center gap-3">
      <b-button variant="warning" class="rounded-pill px-4 py-2 font-weight-bold shadow-md gold-gradient-btn" @click="$router.push({ path: '/subscription', query: { module: 'q_hotking_v2' }})">
        前往解锁
      </b-button>
      <b-button variant="outline-light" class="rounded-pill px-4 py-2 font-weight-bold shadow-md" @click="$router.back()">
        返回
      </b-button>
    </div>
  </div>

    <!-- 右侧个股K线与板块详情联动侧边栏 -->
    <StockInfoSidebar 
      :symbol_code="sidebarSymbolCode" 
      :symbol_name="sidebarSymbolName" 
      :date_mark="sidebarDateMark" 
    />
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import ECharts from 'vue-echarts'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'

export default {
  name: 'WonderStrategyDetail',
  components: {
    'v-chart': ECharts,
    StockInfoSidebar
  },
  data() {
    return {
      theme: 'light',
      hasPermission: true, // 是否有权限访问
      permissionMessage: '', // 权限校验失败的提示信息
      
      // 侧边栏个股详情配置
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      sidebarDateMark: '',
      
      chartLoading: false,
      detailsLoading: false,
      equityData: [], // 资金曲线数据
      latestEquity: 1000000.0, // 最新总权益
      selectedDate: '', // 当前选中的审计日期
      performance: {
        total_return_pct: 0.0,
        win_rate_pct: 0.0,
        avg_return_pct: 0.0,
        total_trades: 0
      },
      
      // 流水表格配置
      dailyRecords: [],
      recordFields: [
        { key: 'symbol_name', label: '标的信息', class: 'align-middle' },
        { key: 'trade_time', label: '操作时间', class: 'text-center align-middle d-none d-sm-table-cell', thClass: 'text-center d-none d-sm-table-cell' },
        { key: 'trade_type', label: '方向', class: 'text-center align-middle', thClass: 'text-center' },
        { key: 'trade_price', label: '成交价', class: 'text-right align-middle', thClass: 'text-right' },
        { key: 'trade_amount', label: '股数', class: 'text-right align-middle d-none d-sm-table-cell', thClass: 'text-right d-none d-sm-table-cell' },
        { key: 'trade_balance', label: '成交额', class: 'text-right align-middle d-none d-md-table-cell', thClass: 'text-right d-none d-md-table-cell' },
        { key: 'today_pnl', label: '当日盈亏', class: 'text-right align-middle', thClass: 'text-right' },
        { key: 'total_pnl', label: '总盈亏', class: 'text-right align-middle', thClass: 'text-right' },
        { key: 'commission_balance', label: '手续费', class: 'text-right align-middle text-muted d-none d-md-table-cell', thClass: 'text-right d-none d-md-table-cell' }
      ],

      // 持仓表格配置
      dailyPositions: [],
      positionFields: [
        { key: 'symbol_name', label: '持仓标的', class: 'align-middle' },
        { key: 'total_amount', label: '总持仓', class: 'text-right align-middle d-none d-sm-table-cell', thClass: 'text-right d-none d-sm-table-cell' },
        { key: 'avg_cost', label: '开仓均价', class: 'text-right align-middle', thClass: 'text-right' },
        { key: 'last_price', label: '结算价', class: 'text-right align-middle d-none d-md-table-cell', thClass: 'text-right d-none d-md-table-cell' },
        { key: 'position_pnl', label: '盈亏', class: 'text-right align-middle', thClass: 'text-right' },
        { key: 'status', label: '状态', class: 'text-center align-middle d-none d-sm-table-cell', thClass: 'text-center d-none d-sm-table-cell' }
      ],
      userRoles: []
    }
  },
  computed: {
    // 动态算实时收益率
    realTimeReturnPct() {
      if (this.latestEquity) {
        return ((this.latestEquity - 1000000.0) / 1000000.0) * 100
      }
      return this.performance.total_return_pct || 0.0
    },
    monthlyYields() {
      if (!this.equityData || this.equityData.length === 0) return []

      // 1. 按日期升序排列数据
      const sortedData = [...this.equityData].sort((a, b) => a.date.localeCompare(b.date))
      
      // 2. 计算策略初始资金
      const firstNode = sortedData[0]
      const startingCapital = firstNode ? (firstNode.equity - (firstNode.income_balance || 0)) : 1000000.0

      // 3. 找出每个月份的最后一个交易日节点
      const monthLastNodes = {} // key: "YYYY-MM", value: node
      sortedData.forEach(node => {
        const key = node.date.substring(0, 7) // "YYYY-MM"
        monthLastNodes[key] = node
      })

      // 4. 获取所有有数据的月份键并排序
      const monthKeys = Object.keys(monthLastNodes).sort()

      // 5. 计算每个有数据月份的收益率
      const calculatedYieldMap = {} // key: "YYYY-MM", value: yieldVal
      monthKeys.forEach((key, index) => {
        const curLastNode = monthLastNodes[key]
        let baseEquity = startingCapital
        if (index > 0) {
          const prevKey = monthKeys[index - 1]
          baseEquity = monthLastNodes[prevKey].equity
        }
        const yieldVal = baseEquity > 0 ? ((curLastNode.equity - baseEquity) / baseEquity) * 100 : 0.0
        calculatedYieldMap[key] = yieldVal
      })

      // 6. 获取所有不同年份并降序排列
      const years = [...new Set(monthKeys.map(key => key.substring(0, 4)))].sort((a, b) => b.localeCompare(a))

      const CHINESE_MONTHS = ['', '一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']

      // 7. 对每一年生成完整的 12 个月数据
      const result = years.map(yearStr => {
        const year = parseInt(yearStr)
        const months = []
        let maxYield = 0.0
        let bestMonthIndex = -1

        for (let m = 1; m <= 12; m++) {
          const key = `${yearStr}-${String(m).padStart(2, '0')}`
          const hasData = key in calculatedYieldMap
          const yieldVal = hasData ? calculatedYieldMap[key] : null

          months.push({
            month: m,
            monthName: CHINESE_MONTHS[m],
            hasData,
            yield: yieldVal,
            isBest: false
          })

          // 记录每年的最佳月份 (正收益且最高)
          if (hasData && yieldVal > maxYield) {
            maxYield = yieldVal
            bestMonthIndex = m - 1
          }
        }

        // 标记最佳月份
        if (bestMonthIndex !== -1) {
          months[bestMonthIndex].isBest = true
        }

        return {
          year,
          months
        }
      })

      return result
    },
    isAdmin() {
      return this.userRoles && this.userRoles.includes('admin')
    },
    // 动态计算持仓表格的列（只要是今天或者包含了实时估值字段就追加）
    computedPositionFields() {
      const fields = [...this.positionFields];
      const todayStr = this.getTodayDateStr();
      const hasIntraday = this.dailyPositions.some(p => p.today_change_pct !== undefined);
      
      if (hasIntraday || this.selectedDate === todayStr) {
        const lastPriceIdx = fields.findIndex(f => f.key === 'last_price');
        if (lastPriceIdx !== -1) {
          fields.splice(lastPriceIdx + 1, 0, 
            { key: 'today_change_pct', label: '当前涨幅', class: 'text-right align-middle font-weight-bold', thClass: 'text-right' },
            { key: 'today_pnl', label: '当日盈亏', class: 'text-right align-middle font-weight-bold', thClass: 'text-right' }
          );
        }
      }
      
      // 如果是管理员，动态添加操作列支持手动卖出
      if (this.isAdmin) {
        fields.push({ key: 'actions', label: '操作', class: 'text-center align-middle', thClass: 'text-center' });
      }
      
      return fields;
    },
    // 判断当前是否是交易日且在 09:25 到 15:00 之间
    isTradingHours() {
      const now = new Date();
      const day = now.getDay();
      const isWeekday = day >= 1 && day <= 5;
      const hh = now.getHours();
      const mm = now.getMinutes();
      const timeVal = hh * 100 + mm;
      return isWeekday && (timeVal >= 925 && timeVal <= 1500);
    },
    // ECharts 配置生成器
    chartOptions() {
      const dates = this.equityData.map(item => item.date)
      const equities = this.equityData.map(item => item.equity)
      
      // 动态计算每日盈亏百分比
      const dailyPnlPcts = this.equityData.map((item, idx) => {
        if (idx === 0) return 0.0
        const prev = this.equityData[idx - 1].equity
        if (!prev) return 0.0
        const cur = item.equity
        return parseFloat((((cur - prev) / prev) * 100).toFixed(4))
      })

      let minEquity = 1000000.0
      let maxEquity = 2500000.0
      if (equities.length > 0) {
        const rawMin = Math.min(...equities)
        const rawMax = Math.max(...equities)
        const range = rawMax - rawMin
        const padding = range * 0.08 || 100000
        minEquity = Math.max(0, rawMin - padding)
        maxEquity = rawMax + padding
      }
      
      const isDark = this.theme === 'dark'
      const labelColor = isDark ? '#94a3b8' : '#475569'
      
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: isDark ? '#475569' : '#cbd5e1',
              width: 1,
              type: 'dashed'
            }
          },
          formatter: (params) => {
            const index = params[0].dataIndex
            const dataObj = this.equityData[index]
            if (!dataObj) return ''
            
            const incomeSign = dataObj.income_balance >= 0 ? '+' : ''
            const pnlColor = dataObj.income_balance >= 0 ? '#ef4444' : '#10b981' // A股红涨绿跌
            
            // 动态计算该时点对应的收益百分比
            const yieldPct = ((dataObj.equity - 1000000.0) / 1000000.0) * 100
            const yieldSign = yieldPct >= 0 ? '+' : ''
            const yieldColor = yieldPct >= 0 ? '#ef4444' : '#10b981'
            
            // 动态计算该时点的当日盈亏百分比
            const dayPnlPct = index === 0 ? 0.0 : ((dataObj.equity - this.equityData[index - 1].equity) / this.equityData[index - 1].equity) * 100
            const dayPnlSign = dayPnlPct >= 0 ? '+' : ''
            const dayPnlColor = dayPnlPct >= 0 ? '#ef4444' : '#10b981'

            return `
              <div style="font-family: sans-serif; padding: 4px 8px;">
                <div style="font-weight: bold; margin-bottom: 6px; color: ${isDark ? '#f8fafc' : '#1e293b'}">${dataObj.date}</div>
                <div style="margin-bottom: 4px; display:flex; justify-content:space-between; gap:20px;">
                  <span style="color:${labelColor}">总账户净资产:</span>
                  <span style="font-weight:bold; color:#f59e0b">${this.formatMoney(dataObj.equity)}</span>
                </div>
                <div style="margin-bottom: 4px; display:flex; justify-content:space-between; gap:20px;">
                  <span style="color:${labelColor}">收益百分比:</span>
                  <span style="font-weight:bold; color:${yieldColor}">${yieldSign}${yieldPct.toFixed(2)}%</span>
                </div>
                <div style="margin-bottom: 4px; display:flex; justify-content:space-between; gap:20px;">
                  <span style="color:${labelColor}">当日盈亏百分比:</span>
                  <span style="font-weight:bold; color:${dayPnlColor}">${dayPnlSign}${dayPnlPct.toFixed(2)}%</span>
                </div>
                <div style="margin-bottom: 4px; display:flex; justify-content:space-between; gap:20px;">
                  <span style="color:${labelColor}">可用现金:</span>
                  <span style="font-weight:bold; color:${isDark ? '#e2e8f0' : '#475569'}">${this.formatMoney(dataObj.cash_balance)}</span>
                </div>
                <div style="margin-bottom: 4px; display:flex; justify-content:space-between; gap:20px;">
                  <span style="color:${labelColor}">股票持仓市值:</span>
                  <span style="font-weight:bold; color:#3b82f6">${this.formatMoney(dataObj.market_value)}</span>
                </div>
                <div style="display:flex; justify-content:space-between; gap:20px;">
                  <span style="color:${labelColor}">累计超额回报:</span>
                  <span style="font-weight:bold; color:${pnlColor}">${incomeSign}${this.formatMoney(dataObj.income_balance)}</span>
                </div>
              </div>
            `
          },
          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: isDark ? '#334155' : '#e2e8f0',
          borderWidth: 1,
          textStyle: {
            color: isDark ? '#f1f5f9' : '#334155'
          }
        },
        axisPointer: {
          link: { xAxisIndex: 'all' }
        },
        grid: [
          // 上半部：总净值曲线图 (使用固定像素边距确保完美上下对齐)
          {
            left: 65,
            right: 65,
            top: '6%',
            height: '52%'
          },
          // 下半部：当日盈亏百分比柱状图 (使用固定像素边距确保完美上下对齐)
          {
            left: 65,
            right: 65,
            top: '68%',
            height: '24%'
          }
        ],
        xAxis: [
          // 上网格X轴：不显示刻度字，与下轴共用日期
          {
            gridIndex: 0,
            type: 'category',
            data: dates,
            boundaryGap: false,
            axisLine: {
              lineStyle: {
                color: isDark ? '#334155' : '#e2e8f0'
              }
            },
            axisLabel: {
              show: false
            },
            axisTick: {
              show: false
            }
          },
          // 下网格X轴：显示刻度日期
          {
            gridIndex: 1,
            type: 'category',
            data: dates,
            boundaryGap: false,
            axisLine: {
              lineStyle: {
                color: isDark ? '#334155' : '#e2e8f0'
              }
            },
            axisLabel: {
              color: labelColor,
              fontSize: 10,
              interval: Math.ceil(dates.length / 10)
            }
          }
        ],
        yAxis: [
          // 上网格左Y轴：资产数值（万）
          {
            gridIndex: 0,
            type: 'value',
            min: minEquity,
            max: maxEquity,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              color: labelColor,
              fontSize: 10,
              formatter: (val) => {
                return (val / 10000).toFixed(0) + '万'
              }
            },
            splitLine: {
              lineStyle: {
                color: isDark ? '#1e293b' : '#f1f5f9'
              }
            }
          },
          // 上网格右Y轴：累计收益百分比
          {
            gridIndex: 0,
            type: 'value',
            min: minEquity,
            max: maxEquity,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              color: labelColor,
              fontSize: 10,
              formatter: (val) => {
                const ratio = ((val - 1000000.0) / 1000000.0) * 100
                return (ratio >= 0 ? '+' : '') + ratio.toFixed(1) + '%'
              }
            },
            splitLine: {
              show: false
            }
          },
          // 下网格左Y轴：当日盈亏百分比值
          {
            gridIndex: 1,
            type: 'value',
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              color: labelColor,
              fontSize: 10,
              formatter: (val) => {
                return (val >= 0 ? '+' : '') + val.toFixed(1) + '%'
              }
            },
            splitLine: {
              lineStyle: {
                color: isDark ? '#1e293b' : '#f1f5f9',
                type: 'dashed'
              }
            }
          }
        ],
        series: [
          // 上网格：总净资产曲线
          {
            name: '总净资产',
            type: 'line',
            xAxisIndex: 0,
            yAxisIndex: 0,
            data: equities,
            showSymbol: false,
            symbol: 'circle',
            itemStyle: {
              color: '#ef4444'
            },
            lineStyle: {
              width: 3,
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 1, y2: 0,
                colorStops: [
                  { offset: 0, color: '#f59e0b' },
                  { offset: 1, color: '#ef4444' }
                ]
              }
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(239, 68, 68, 0.22)' },
                  { offset: 1, color: 'rgba(239, 68, 68, 0.0)' }
                ]
              }
            }
          },
          // 下网格：当日盈亏百分比柱状图
          {
            name: '当日盈亏百分比',
            type: 'bar',
            xAxisIndex: 1,
            yAxisIndex: 2,
            data: dailyPnlPcts,
            barMaxWidth: 15,
            itemStyle: {
              color: (params) => {
                return params.value >= 0 ? '#ef4444' : '#10b981' // A股红涨绿跌
              },
              borderRadius: [2, 2, 0, 0]
            }
          }
        ]
      }
    }
  },
  mounted() {
    // 监听全局主题事件
    try {
      const t = localStorage.getItem('site.theme')
      if (t) this.theme = t
    } catch (e) {
      console.warn('Theme read error:', e)
    }

    this.onThemeChanged = (e) => {
      this.theme = e && e.detail && e.detail.theme ? e.detail.theme : (localStorage.getItem('site.theme') || 'light')
    }
    window.addEventListener('site-theme-changed', this.onThemeChanged)

    // 加载管理员角色
    this.updateUserRoles()
    this.onProfileUpdated = (e) => {
      const u = e && e.detail ? e.detail : {}
      const r = u.roles
      this.userRoles = Array.isArray(r) ? r : String(r||'').split(',').map(s=>s.trim()).filter(Boolean)
    }
    window.addEventListener('user-profile-updated', this.onProfileUpdated)

    // 检测并保存 URL 中的分享码
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const urlShareCode = urlParams.get('share_code')
      if (urlShareCode) {
        localStorage.setItem('strategy_share_code', urlShareCode)
        console.log('[Share] Saved strategy share code:', urlShareCode)
      }
    } catch (e) {
      console.error('Failed to parse share_code from URL:', e)
    }

    // 初始化加载
    this.fetchEquityCurve()
    this.fetchStrategyPerformance()
  },
  beforeDestroy() {
    window.removeEventListener('site-theme-changed', this.onThemeChanged)
    window.removeEventListener('user-profile-updated', this.onProfileUpdated)
  },
  methods: {
    // 刷新全部策略数据
    refreshAllData() {
      this.fetchEquityCurve()
      this.fetchStrategyPerformance()
    },

    async generateShareLink() {
      try {
        this.detailsLoading = true
        const res = await request_zzquant({
          url: '/v3/quant/strategy/share-token',
          method: 'post'
        })
        if (res.data && res.data.code === 20000) {
          const shareCode = res.data.data.share_code
          const origin = window.location.origin
          const shareUrl = `${origin}/wonder/strategy/detail?share_code=${shareCode}`
          
          await navigator.clipboard.writeText(shareUrl)
          
          this.$bvToast.toast('分享链接已复制到剪贴板，有效期 12 小时！', {
            title: '复制成功 🔗',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          })
        } else {
          this.$bvToast.toast(res.data.message || '生成分享链接失败', {
            title: '操作失败',
            variant: 'danger',
            solid: true
          })
        }
      } catch (err) {
        console.error('Generate share token error:', err)
        const msg = (err.response && err.response.data && err.response.data.detail) || err.message
        this.$bvToast.toast(msg || '请求失败，请稍后重试', {
          title: '操作失败',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.detailsLoading = false
      }
    },

    isBoughtToday(symbolCode) {
      console.log('isBoughtToday', symbolCode)
      // return false
      return this.dailyRecords && this.dailyRecords.some(r => r.symbol_code === symbolCode && r.trade_type === '买入')
    },

    updateUserRoles() {
      try {
        const raw = localStorage.getItem('user_info')
        if(!raw) { this.userRoles = []; return }
        const u = JSON.parse(raw)
        const r = u && u.roles
        this.userRoles = Array.isArray(r) ? r : String(r||'').split(',').map(s=>s.trim()).filter(Boolean)
      } catch(e) { this.userRoles = [] }
    },

    async triggerManualSell(item) {
      if (!item) return
      
      const confirm = await this.$bvModal.msgBoxConfirm(
        `确定要对持仓股票 ${item.symbol_name} (${item.symbol_code}) 执行手动市价平仓卖出吗？`,
        {
          title: '手动平仓确认',
          okVariant: 'danger',
          okTitle: '确认卖出',
          cancelTitle: '取消',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )
      
      if (!confirm) return
      
      try {
        this.detailsLoading = true
        const res = await request_zzquant({
          url: '/v3/quant/strategy/sell',
          method: 'post',
          data: {
            symbol_code: item.symbol_code
          }
        })
        
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast(res.data.msg || '平仓指令执行成功！', {
            title: '操作成功',
            variant: 'success',
            solid: true
          })
          // 重新拉取当日明细与曲线以更新 UI
          await this.fetchDailyDetails()
          await this.fetchEquityCurve()
        } else {
          this.$bvToast.toast(res.data.msg || '平仓指令执行失败', {
            title: '操作失败',
            variant: 'danger',
            solid: true
          })
        }
      } catch (err) {
        console.error('Manual sell error:', err)
        const msg = (err.response && err.response.data && err.response.data.detail) || err.message
        this.$bvToast.toast(msg || '请求执行失败，请稍后重试', {
          title: '操作失败',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.detailsLoading = false
      }
    },

    // 拉取策略的核心业绩指标
    async fetchStrategyPerformance() {
      try {
        const res = await request_zzquant({
          url: '/v3/quant/strategy/performance',
          method: 'get',
          noRedirect: true // 开启无跳转，未登录/无权限不打断曲线渲染
        })
        if (res.data && res.data.code === 20000) {
          this.performance = res.data.data || {
            total_return_pct: 0.0,
            win_rate_pct: 0.0,
            avg_return_pct: 0.0,
            total_trades: 0
          }
        }
      } catch (err) {
        console.error('Fetch strategy performance error:', err)
        const status = err.status || (err.response && err.response.status) || (err.__unauthorized ? 401 : null)
        const msg = (err.response && err.response.data && err.response.data.detail) || err.message
        if (status === 403 || status === 401) {
          this.hasPermission = false
          this.permissionMessage = msg || '该模块需要有效订阅“策略模拟V2”方可访问。'
        }
      }
    },

    // 拉取策略的全部净值记录
    async fetchEquityCurve() {
      this.chartLoading = true
      try {
        const res = await request_zzquant({
          url: '/v3/quant/strategy/equity',
          method: 'get',
          noRedirect: true // 开启无跳转，未登录/无权限不打断曲线渲染
        })
        
        if (res.data && res.data.code === 20000) {
          const list = res.data.data || []
          this.equityData = list
          if (list.length > 0) {
            // 获取最新一天的数据
            const latest = list[list.length - 1]
            this.latestEquity = latest.equity
            this.selectedDate = latest.date // 默认选中最新一天
            
            // 自动加载最新一天的交易记录和持仓快照
            await this.fetchDailyDetails()
          }
        }
      } catch (err) {
        console.error('Fetch equity curve error:', err)
        const status = err.status || (err.response && err.response.status) || (err.__unauthorized ? 401 : null)
        const msg = (err.response && err.response.data && err.response.data.detail) || err.message
        if (status === 403 || status === 401) {
          this.hasPermission = false
          this.permissionMessage = msg || '该模块需要有效订阅“策略模拟V2”方能解锁。'
        } else {
          this.$bvToast.toast('读取策略资金曲线失败，请稍后重试', { variant: 'danger', solid: true })
        }
      } finally {
        this.chartLoading = false
      }
    },

    // 拉取某一天的交易明细与持仓快照
    async fetchDailyDetails() {
      if (!this.selectedDate) return
      this.detailsLoading = true
      try {
        const res = await request_zzquant({
          url: '/v3/quant/strategy/daily-details',
          method: 'get',
          params: {
            date: this.selectedDate
          },
          noRedirect: true // 开启无跳转，未登录/无权限不打断曲线渲染
        })
        
        if (res.data && res.data.code === 20000) {
          const data = res.data.data || {}
          this.dailyRecords = data.records || []
          this.dailyPositions = data.positions || []
          
          // 仅在盘中使用当日盈亏动态覆盖最后一个节点。
          // 收盘后应以 /strategy/equity 返回的日终快照为准，避免把正确快照再次覆盖成估算值。
          const todayStr = this.getTodayDateStr();
          if (this.isTradingHours && this.selectedDate === todayStr && data.today_pnl_sum !== undefined) {
            if (this.equityData.length >= 2) {
              const lastIdx = this.equityData.length - 1;
              if (this.equityData[lastIdx].date === todayStr) {
                const prevEquity = this.equityData[lastIdx - 1].equity;
                const updatedEquity = prevEquity + data.today_pnl_sum;
                
                // 动态更新今日股票持仓市值
                const curPosVal = this.dailyPositions
                  .filter(p => p.status === '持有中' || p.status === 1)
                  .reduce((sum, p) => sum + (p.market_value || 0), 0);
                  
                // 使用 Vue.set 进行响应式更新，触发 computed/ECharts 重新渲染
                const updatedItem = {
                  ...this.equityData[lastIdx],
                  equity: updatedEquity,
                  total_balance: updatedEquity,
                  income_balance: updatedEquity - 1000000.0,
                  market_value: curPosVal
                };
                this.$set(this.equityData, lastIdx, updatedItem);
                this.latestEquity = updatedEquity;
              }
            }
          }
        }
      } catch (err) {
        console.error('Fetch daily details error:', err)
        const status = err.status || (err.response && err.response.status) || (err.__unauthorized ? 401 : null)
        const msg = (err.response && err.response.data && err.response.data.detail) || err.message
        if (status === 403 || status === 401) {
          this.hasPermission = false
          this.permissionMessage = msg || '该模块需要有效订阅题材表格方可访问。'
        } else {
          this.$bvToast.toast(`查询 ${this.selectedDate} 明细失败`, { variant: 'danger', solid: true })
        }
      } finally {
        this.detailsLoading = false
      }
    },

    // 呼叫右侧个股详情抽屉并标注选中日期
    openSidebar(item) {
      if (!item) return
      this.sidebarSymbolCode = item.symbol_code || ''
      this.sidebarSymbolName = item.symbol_name || ''
      this.sidebarDateMark = this.selectedDate || ''
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
    },

    // ECharts 点击定位日期监听
    onChartClick(params) {
      if (params && params.name) {
        // params.name 包含了点击点的 x 轴刻度名 (日期，格式：YYYY-MM-DD)
        this.selectedDate = params.name
        
        // 优雅提示用户已定位
        this.$bvToast.toast(`已自动定位至：${this.selectedDate}`, {
          title: '日期切换 🎯',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        })
        
        // 触发查询
        this.fetchDailyDetails()
      }
    },

    getTooltipContent(year, monthData) {
      if (!monthData.hasData) {
        return `<div class="p-1 text-center font-weight-bold" style="font-size: 11px;">${year}年${monthData.monthName}</div><div class="text-muted text-center" style="font-size: 10px;">未开始/无数据</div>`
      }
      const sign = monthData.yield >= 0 ? '+' : ''
      const bestText = monthData.isBest ? ' 👍 最佳' : ''
      const color = monthData.yield >= 0 ? '#ef4444' : '#60a5fa'
      return `
        <div class="p-1 text-left" style="font-size: 11px; line-height: 1.4;">
          <b class="d-block mb-1">${year}年${monthData.monthName}${bestText}</b>
          <span style="color: #94a3b8;">月收益率:</span> 
          <span style="color: ${color}; font-weight: bold;">${sign}${monthData.yield.toFixed(2)}%</span>
        </div>
      `
    },

    // 格式化货币
    formatMoney(val) {
      if (val === undefined || val === null) return '0.00'
      return val.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' 元'
    },
    // 缩略货币
    formatCompactMoney(val) {
      if (val === undefined || val === null) return '0.00'
      if (val >= 10000) {
        return (val / 10000).toFixed(2) + ' 万'
      }
      return val.toFixed(2)
    },
    // 获取持仓盈亏金额
    getPnlAmount(item) {
      if (item.status === '持有中' || item.status === 1) {
        const pnl = (item.last_price - item.avg_cost) * item.total_amount
        return (pnl >= 0 ? '+' : '') + pnl.toFixed(2)
      } else {
        const pnl = item.realized_pnl || 0
        return (pnl >= 0 ? '+' : '') + pnl.toFixed(2)
      }
    },
    // 获取持仓盈亏比率
    getPnlRatio(item) {
      if (!item.avg_cost) return '0.00%'
      const ratio = ((item.last_price - item.avg_cost) / item.avg_cost) * 100
      return (ratio >= 0 ? '+' : '') + ratio.toFixed(2) + '%'
    },
    // 获取持仓盈亏颜色 (红涨绿跌)
    getPnlColor(item) {
      let pnl = 0
      if (item.status === '持有中' || item.status === 1) {
        pnl = (item.last_price - item.avg_cost) * item.total_amount
      } else {
        pnl = item.realized_pnl || 0
      }
      return pnl >= 0 ? 'pnl-red' : 'pnl-green'
    },
    // 获取当前日期字符串 (YYYY-MM-DD)
    getTodayDateStr() {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
  }
}
</script>

<style scoped>
/* 基本样式与主题配色 */
.strategy-detail-page {
  min-height: calc(100vh - 120px);
  position: relative !important;
  background-color: var(--bg-main, #f8fafc) !important;
  color: var(--text-main, #334155) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  transition: background-color 0.3s ease, color 0.3s ease;
}

/* 顶部标题与图标的文字样式 */
.strategy-detail-page h5,
.strategy-detail-page h3,
.strategy-detail-page span.font-weight-bold {
  color: var(--text-main) !important;
}

.text-muted {
  color: var(--text-muted) !important;
}

/* 尊享黑金质感介绍卡 */
.premium-intro-card {
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.dark-theme .premium-intro-card {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid rgba(255, 193, 7, 0.25) !important;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
}

.light-theme .premium-intro-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid var(--border-color) !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
}

.premium-card-bg {
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  background: radial-gradient(circle at 80% 20%, rgba(245, 158, 11, 0.12) 0%, transparent 50%);
  pointer-events: none;
}

.light-theme .premium-card-bg {
  background: radial-gradient(circle at 80% 20%, rgba(245, 158, 11, 0.06) 0%, transparent 50%);
}

.gold-text {
  color: #fbbf24;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
}

.light-theme .gold-text {
  color: #b45309 !important;
  text-shadow: none;
}

.premium-badge {
  background: rgba(245, 158, 11, 0.15) !important;
  color: #fbbf24 !important;
  border: 1px solid rgba(245, 158, 11, 0.3);
  font-weight: bold;
}

/* 悬浮态 */
.hover-primary:hover {
  color: #3b82f6 !important;
}

/* 卡片定制 */
.custom-card {
  background-color: var(--bg-card) !important;
  color: var(--text-main) !important;
  border: 1px solid var(--border-color) !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
}

.dark-theme .custom-card {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

.custom-card-header {
  border-bottom: 1px solid var(--border-color) !important;
}

/* ECharts 图表容器 */
.equity-chart-card {
  border-radius: 16px;
}

.chart-loading-overlay {
  background: rgba(255, 255, 255, 0.85);
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 10;
}

.dark-theme .chart-loading-overlay {
  background: rgba(15, 23, 42, 0.85);
}

/* 交互型日期选择器 */
.custom-datepicker {
  background-color: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--text-main) !important;
}

/* 表格定制 */
.table-responsive ::v-deep table {
  color: var(--text-main) !important;
  background-color: transparent !important;
}

.table-responsive ::v-deep thead th {
  background-color: var(--bg-table-header) !important;
  color: var(--text-muted) !important;
  border-bottom: 2px solid var(--border-color) !important;
  border-top: none !important;
}

.table-responsive ::v-deep tbody td {
  border-top: 1px solid var(--border-color) !important;
}

.table-responsive ::v-deep .table-striped tbody tr:nth-of-type(odd) {
  background-color: var(--bg-muted) !important;
}

.table-responsive ::v-deep .table-striped tbody tr:nth-of-type(even) {
  background-color: var(--bg-card) !important;
}

.table-responsive ::v-deep .table-hover tbody tr:hover {
  background-color: var(--bg-muted) !important;
  opacity: 0.95;
}

.table-responsive {
  max-height: 480px;
  overflow-y: auto;
}

/* 强制提升浅色模式页面整体对比度，避免过度扁平 */
.strategy-detail-page.light-theme {
  background-color: #f1f5f9 !important;
  color: #1e293b !important;
}

.strategy-detail-page.dark-theme {
  background-color: #0b0f19 !important;
  color: #cbd5e1 !important;
}

/* 解决浅色与深色主题下的日期选择器样式问题 */
.strategy-detail-page.light-theme .custom-datepicker {
  background-color: #ffffff !important;
  color: #1e293b !important;
  border-color: #cbd5e1 !important;
}

.strategy-detail-page.dark-theme .custom-datepicker {
  background-color: #1e293b !important;
  color: #f1f5f9 !important;
  border-color: #334155 !important;
}

/* 强制确保尊享黑金介绍卡内的文本可读性，避免被全局浅色模式样式覆盖 */
.dark-theme .premium-intro-card,
.dark-theme .premium-intro-card .intro-desc,
.dark-theme .premium-intro-card .metric-item span:first-child,
.dark-theme .premium-intro-card .equity-badge span {
  color: rgba(255, 255, 255, 0.85) !important;
}

.dark-theme .premium-intro-card h3,
.dark-theme .premium-intro-card .gold-text {
  color: #fbbf24 !important;
}

.dark-theme .premium-intro-card .metric-value.text-success {
  color: #34d399 !important; /* 极客绿 */
}

.dark-theme .premium-intro-card .metric-value.text-warning {
  color: #fbbf24 !important; /* 金黄色 */
}

.dark-theme .premium-intro-card .metric-value.text-info {
  color: #60a5fa !important; /* 浅蓝色 */
}

.dark-theme .premium-intro-card .metric-value.text-light {
  color: #f1f5f9 !important; /* 极客白 */
}

/* 浅色模式尊享卡样式 */
.light-theme .premium-intro-card {
  color: var(--text-main) !important;
}

.light-theme .premium-intro-card .intro-desc {
  color: var(--text-muted) !important;
}

.light-theme .premium-intro-card h3 {
  color: var(--text-main) !important;
}

.light-theme .premium-intro-card .gold-text {
  color: #b45309 !important;
}

.light-theme .premium-intro-card .metric-value.text-success {
  color: #10b981 !important; /* A股绿色或者标准绿 */
}

.light-theme .premium-intro-card .metric-value.text-warning {
  color: #d97706 !important; /* 深黄/橙 */
}

.light-theme .premium-intro-card .metric-value.text-info {
  color: #2563eb !important; /* 蓝色 */
}

.light-theme .premium-intro-card .metric-value.text-light {
  color: var(--text-main) !important; /* 浅色模式下不要用白色，用主文字色 */
}

/* 分割线和指标列的样式 */
.premium-divider {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.light-theme .premium-divider {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.premium-metrics-col {
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.light-theme .premium-metrics-col {
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.premium-metric-label {
  color: rgba(255, 255, 255, 0.5) !important;
}
.light-theme .premium-metric-label {
  color: var(--text-muted) !important;
}

/* 浅色模式全局样式注入适配 */
.light-theme {
  --bg-main: #f8fafc;
  --bg-card: #ffffff;
  --bg-muted: #f1f5f9;
  --border-color: #cbd5e1;
  --text-main: #1e293b;
  --text-muted: #64748b;
  --bg-table-header: #f1f5f9;
}

/* 深色模式全局样式注入适配 */
.dark-theme {
  --bg-main: #0b0f19;
  --bg-card: #151d30;
  --bg-muted: #1e293b;
  --border-color: #243049;
  --text-main: #cbd5e1;
  --text-muted: #64748b;
  --bg-table-header: #1e293b;
}

/* 软化型 Badge 状态 */
.soft-danger {
  background-color: rgba(239, 68, 68, 0.12) !important;
  color: #f87171 !important;
}

.btn-secret {
  cursor: pointer !important;
  color: var(--text-muted) !important;
  user-select: none !important;
  font-weight: bold !important;
  font-size: 14px !important;
  padding: 0 8px !important;
  transition: color 0.15s ease-in-out !important;
}

.btn-secret:hover {
  color: #ef4444 !important; /* 悬浮时低调显现红色 */
}

.soft-success {
  background-color: rgba(16, 185, 129, 0.12) !important;
  color: #34d399 !important;
}

.soft-info {
  background-color: rgba(59, 130, 246, 0.12) !important;
  color: #60a5fa !important;
}

.soft-warning {
  background-color: rgba(245, 158, 11, 0.12) !important;
  color: #fbbf24 !important;
}

/* 其它定制 */
.z-index-1 {
  z-index: 1;
}

.gap-3 {
  gap: 1rem;
}

.hover-scale {
  transition: transform 0.2s ease;
}
.hover-scale:hover {
  transform: scale(1.05);
}

/* A股红涨绿跌强制高亮，提权以覆盖全局 font-weight-bold 样式 */
.strategy-detail-page span.font-weight-bold.pnl-red,
.strategy-detail-page span.pnl-red,
.strategy-detail-page .pnl-red {
  color: #ef4444 !important; /* A股正红 */
}

.strategy-detail-page span.font-weight-bold.pnl-green,
.strategy-detail-page span.pnl-green,
.strategy-detail-page .pnl-green {
  color: #10b981 !important; /* A股绿 */
}

/* 浅色模式与深色模式下金黄色对比度自适应 */
.gold-text {
  color: #d97706 !important; /* 浅色模式下采用高对比度琥珀金，确保极佳的可读性 */
}

.dark-theme .gold-text {
  color: #fbbf24 !important; /* 深色模式下还原为尊贵亮金黄 */
}

/* 响应式图表高度控制 */
.responsive-chart-body {
  height: 350px !important;
}

@media (min-width: 768px) {
  .responsive-chart-body {
    height: 460px !important;
  }
}

/* 极客玻璃遮罩阻断器 */
.permission-blocked-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 23, 42, 0.65) !important; /* 半透明暗色底色 */
  backdrop-filter: blur(18px) saturate(180%); /* 高强度毛玻璃模糊和饱和度提升 */
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  z-index: 999;
  border-radius: 16px;
  animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.glass-blocked-card {
  background: rgba(30, 41, 59, 0.75) !important;
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  max-width: 480px;
  width: 90%;
  animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* 浅色模式适配 */
.light-theme .permission-blocked-overlay {
  background: rgba(241, 245, 249, 0.6) !important; /* 浅色半透明背景 */
}

.light-theme .glass-blocked-card {
  background: rgba(255, 255, 255, 0.85) !important;
  border: 1px solid rgba(203, 213, 225, 0.6) !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.light-theme .glass-blocked-card h3 {
  color: #1e293b !important;
}

.light-theme .glass-blocked-card p {
  color: #64748b !important;
}

/* 暗金色与锁定图标微调 */
.text-muted-gold {
  color: rgba(251, 191, 36, 0.85) !important;
}

.light-theme .text-muted-gold {
  color: #d97706 !important;
}

.filter-blur {
  filter: blur(5.5px) !important;
  user-select: none !important;
  pointer-events: none !important;
  opacity: 0.65 !important;
  transition: all 0.3s ease;
}

.bg-warning-10 {
  background-color: rgba(245, 158, 11, 0.12) !important;
}

.gold-gradient-btn {
  background: linear-gradient(135deg, #fbbf24, #d97706) !important;
  border: none !important;
  color: #0b0f19 !important;
  transition: all 0.3s ease;
}

.gold-gradient-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.4) !important;
  color: #0b0f19 !important;
}

/* 可点击股票单元格交互特效 */
.clickable-stock-cell {
  cursor: pointer !important;
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease;
  user-select: none;
}

.clickable-stock-cell:hover {
  transform: translateX(4px);
  opacity: 0.9;
}

.clickable-stock-cell:hover .font-weight-bold {
  color: #d97706 !important; /* 浅色模式黄金悬停 */
}

.dark-theme .clickable-stock-cell:hover .font-weight-bold {
  color: #fbbf24 !important; /* 深色模式尊贵亮金悬停 */
}

.clickable-stock-cell:hover .text-muted {
  color: rgba(245, 158, 11, 0.9) !important; /* 代码变更为黄金色，极具视觉一体化 */
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleIn {
  from { transform: scale(0.92); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* 优化扁平化月度收益日历样式 */
.mini-calendar-container {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
}

.mini-year-group {
  display: flex;
  align-items: center;
}

.mini-year-title {
  font-size: 11px;
  font-weight: bold;
  color: #fbbf24 !important; /* 金黄色 */
  opacity: 0.95;
  border-right: 2px solid rgba(251, 191, 36, 0.3);
  padding-right: 8px;
  margin-right: 8px;
  text-shadow: 0 1px 4px rgba(251, 191, 36, 0.25);
  min-width: 42px;
  text-align: right;
}

.light-theme .mini-year-title {
  color: #b45309 !important; /* 暗金色 */
  border-right: 2px solid rgba(180, 83, 9, 0.3);
  text-shadow: none;
}

.mini-month-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
}

.mini-month-box {
  width: 58px;
  height: 36px;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  transition: transform 0.15s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.15s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.25;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.light-theme .mini-month-box {
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.mini-month-box:hover {
  transform: scale(1.12);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
  z-index: 2;
  border-color: rgba(255, 255, 255, 0.25) !important;
}

.light-theme .mini-month-box:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 0, 0, 0.15) !important;
}

.mini-month-label {
  font-size: 10px;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.85);
}

.bg-mini-none .mini-month-label {
  color: rgba(255, 255, 255, 0.35);
}

.light-theme .bg-mini-none .mini-month-label {
  color: rgba(0, 0, 0, 0.4);
}

.mini-month-yield {
  font-size: 9px;
  font-weight: bold;
  color: #ffffff;
}

.bg-mini-none .mini-month-yield {
  color: rgba(255, 255, 255, 0.25);
}

.light-theme .bg-mini-none .mini-month-yield {
  color: rgba(0, 0, 0, 0.3);
}

.bg-mini-up {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.bg-mini-down {
  background: linear-gradient(135deg, #5c8bd6 0%, #4b77d1 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.bg-mini-none {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px dashed rgba(255, 255, 255, 0.12) !important;
}

.light-theme .bg-mini-none {
  background: rgba(0, 0, 0, 0.03) !important;
  border: 1px dashed rgba(0, 0, 0, 0.12) !important;
}

.mini-best-icon {
  font-size: 8px;
  color: #fbbf24;
}

@media (max-width: 991px) {
  /* On tablets and mobile, remove vertical right border to avoid awkward spacing */
  .premium-intro-card .premium-metrics-col {
    border-right: none !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
    padding-bottom: 20px;
    margin-bottom: 20px;
  }
  .light-theme .premium-intro-card .premium-metrics-col {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
  }
}
</style>
